import type {
  ConnectionStatusDto,
  CreatePrintSetBody,
  LeadDetailDto,
  LeadDto,
  LeadStatus,
  ListLeadsQuery,
  PrintSetDto,
  SenderProfileDto,
  SenderProfileUpdate,
  SuppressionDto,
  TemplateDto,
  TemplateVersionDto,
  TerritoryDto,
  TerritoryInput,
  TerritoryPreviewDto,
  TerritoryStatsDto,
  TerritoryUpdate,
} from '@reos/acquisition-radar';
import { baseUrl } from './api.js';
import { DomainApiError } from './api-error.js';

/**
 * Krok AKV1 — sekce Akvizice nad `/v1/acquisition-radar/*` (proxy akvizicniho
 * radaru v API REOS). Stejny vzor jako `ai-credits-api.ts`: raw `fetch`, routy
 * jeste nejsou v generovanem `@reos/api-client` (openapi.json regeneruje CI,
 * ROZHODNUTI v reportu). Typy NEJSOU opsane — `import type` z kontextu, takze
 * frontend a API maji jeden zdroj tvaru a do bundlu nejde nic serveroveho
 * (hlida `test/browser-safe-imports.spec.ts`).
 *
 * Kancelar se nikam neposila: API ji bere ze session.
 */

export type {
  ConnectionStatusDto,
  LeadDetailDto,
  LeadDto,
  LeadStatus,
  ListLeadsQuery,
  PrintSetDto,
  SenderProfileDto,
  SuppressionDto,
  TemplateDto,
  TemplateVersionDto,
  TerritoryDto,
  TerritoryInput,
  TerritoryPreviewDto,
  TerritoryStatsDto,
};

const PREFIX = '/v1/acquisition-radar';

/** HTTP 503 = radar neni napojeny nebo neodpovida (sedy stav, ne chyba obrazovky). */
export function isRadarUnavailable(error: unknown): boolean {
  return error instanceof DomainApiError && error.status === 503;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl}${PREFIX}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(init?.headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { detail?: string; title?: string; type?: string } | null;
    // `title` je cesky text serveru (katalog zprav); `detail` u 422 nese duvod radaru.
    const text = [body?.title, body?.detail].filter((part): part is string => typeof part === 'string').join(' ');
    throw new DomainApiError(text === '' ? `HTTP ${String(res.status)}` : text, res.status, body?.type);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function json(method: string, body: unknown): RequestInit {
  return { method, body: JSON.stringify(body) };
}

function query(params: Readonly<Record<string, string | number | undefined>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined) search.set(key, String(value));
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

// ------------------------------------------------------------- napojeni

export function getRadarStatus(): Promise<ConnectionStatusDto> {
  return req('/status');
}

// ---------------------------------------------------------------- uzemi

export function listTerritories(): Promise<TerritoryDto[]> {
  return req('/territories');
}

export function createTerritory(input: TerritoryInput): Promise<TerritoryDto> {
  return req('/territories', json('POST', input));
}

export function previewTerritory(input: TerritoryInput, days = 30): Promise<TerritoryPreviewDto> {
  return req(`/territories/preview${query({ days })}`, json('POST', input));
}

export function updateTerritory(id: number, update: TerritoryUpdate): Promise<TerritoryDto> {
  return req(`/territories/${String(id)}`, json('PATCH', update));
}

export function deleteTerritory(id: number): Promise<void> {
  return req(`/territories/${String(id)}`, { method: 'DELETE' });
}

export function getTerritoryStats(id: number): Promise<TerritoryStatsDto> {
  return req(`/territories/${String(id)}/stats`);
}

// ----------------------------------------------------------------- tipy

export function listLeads(filters: Partial<ListLeadsQuery> = {}): Promise<LeadDto[]> {
  return req(`/leads${query({ ...filters })}`);
}

export function getLead(id: number): Promise<LeadDetailDto> {
  return req(`/leads/${String(id)}`);
}

export function setLeadStatus(id: number, status: LeadStatus): Promise<LeadDto> {
  return req(`/leads/${String(id)}/status`, json('POST', { status }));
}

/** Nahled dopisu — odkaz do noveho okna (prohlizec posle session cookie sam). */
export function letterPreviewUrl(id: number): string {
  return `${baseUrl}${PREFIX}/leads/${String(id)}/letter-preview`;
}

// -------------------------------------------------------------- sablony

export function listTemplates(): Promise<TemplateDto[]> {
  return req('/templates');
}

export function saveTemplate(code: string, text: string, name: string | null): Promise<TemplateDto> {
  return req(`/templates/${encodeURIComponent(code)}`, json('PUT', { text, name }));
}

export function getTemplateHistory(code: string): Promise<TemplateVersionDto[]> {
  return req(`/templates/${encodeURIComponent(code)}/history`);
}

// --------------------------------------------------- profil odesilatele

export function getSenderProfile(): Promise<SenderProfileDto> {
  return req('/sender-profile');
}

export function updateSenderProfile(update: SenderProfileUpdate): Promise<SenderProfileDto> {
  return req('/sender-profile', json('PATCH', update));
}

// ------------------------------------------------------------ vylouceni

export function listSuppression(): Promise<SuppressionDto[]> {
  return req('/suppression');
}

export function addSuppression(address: string, note: string | null): Promise<SuppressionDto> {
  return req('/suppression', json('POST', { address, note }));
}

export function removeSuppression(id: number): Promise<void> {
  return req(`/suppression/${String(id)}`, { method: 'DELETE' });
}

// --------------------------------------------------------- tiskova sada

export function createPrintSet(body: CreatePrintSetBody): Promise<PrintSetDto> {
  return req('/print-sets', json('POST', body));
}

export type PrintSetFile = 'pdf' | 'csv' | 'labels';

/** Soubor tiskove sady — odkaz ke stazeni (stejny vzor jako exporty reportu). */
export function printSetFileUrl(id: number, file: PrintSetFile): string {
  return `${baseUrl}${PREFIX}/print-sets/${String(id)}/${file}`;
}
